import axios from "axios";
import { toast } from "sonner";

import i18n from "@/i18n";

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* Fase E1 — bajo un dominio personalizado el API se consume en RELATIVO
   (mismo origen, proxyado por el edge del dominio); en AOS/preview se usa la
   URL absoluta de siempre. */
const AOS_HOST_SUFFIXES = ["aosequestrian.com", "emergentagent.com", "emergentcf.cloud", "emergent.host", "localhost", "127.0.0.1"];
const _isCustomHost = (() => {
  const h = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
  return h ? !AOS_HOST_SUFFIXES.some((s) => h === s || h === `www.${s}` || h.endsWith(`.${s}`)) : false;
})();
const BACKEND_URL = _isCustomHost ? "" : RAW_BACKEND_URL;

export { BACKEND_URL };

export const TOKEN_KEY = "equus_token";

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* Sesión caducada (Sentry prod: 401 silencioso en /create): un 401 con token
   guardado = JWT expirado/revocado. En zonas protegidas: aviso + redirect a
   /signin conservando el retorno. En páginas públicas: limpieza silenciosa
   (la UI vuelve a estado anónimo, sin expulsar al visitante). Se excluye
   /auth/* (el login legítimo también responde 401 y lo gestiona su página). */
const PROTECTED_PREFIXES = ["/dashboard", "/account", "/create", "/admin"];
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const url = error.config?.url || "";
    if (error.response?.status === 401 && localStorage.getItem(TOKEN_KEY) && !url.includes("/auth/")) {
      localStorage.removeItem(TOKEN_KEY);
      const path = window.location.pathname;
      const isProtected = PROTECTED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
      if (isProtected && !window.__sessionExpiredHandled) {
        window.__sessionExpiredHandled = true;
        toast.error(i18n.t("auth.sessionExpired"));
        const next = encodeURIComponent(path + window.location.search);
        setTimeout(() => window.location.assign(`/signin?next=${next}`), 600);
      }
    }
    return Promise.reject(error);
  }
);

export function getWebSocketUrl(channel) {
  const wsBase = BACKEND_URL.replace(/^http/, "ws");
  return `${wsBase}/api/ws/${channel}`;
}

// Resolve a display URL for a MediaAsset object (authenticated endpoints)
export function assetUrl(asset) {
  if (!asset) return null;
  if (asset.source === "external") return asset.external_url;
  return `${BACKEND_URL}/api/media/file/${asset.id}`;
}

// Resolve from the public media map ({id: {url, ...}}) returned by /api/public/*
export function mediaUrlFromMap(mediaMap, id) {
  const m = mediaMap?.[id];
  if (!m) return null;
  return m.url.startsWith("http") ? m.url : `${BACKEND_URL}${m.url}`;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map(friendlyValidationMessage).filter(Boolean);
    return [...new Set(msgs)].join(" ") || i18n.t("auth.invalidInput");
  }
  if (detail && typeof detail.msg === "string") return friendlyValidationMessage(detail);
  if (detail && typeof detail.code === "string") {
    // backend error codes {code: "..."} -> localized copy (never raw objects)
    const known = i18n.t(`apiErrors.${detail.code}`, { defaultValue: "" });
    return known || i18n.t("auth.invalidInput");
  }
  return String(detail);
}

// Pydantic 422 items -> friendly localized copy. A raw regex/validation
// pattern must NEVER reach the user (client review, Feb 2026).
function friendlyValidationMessage(item) {
  const field = Array.isArray(item?.loc) ? String(item.loc[item.loc.length - 1]) : "";
  const type = String(item?.type || "");
  if (field === "email") return i18n.t("auth.invalidEmail");
  if (field === "password") return i18n.t("auth.passwordTooShort", { min: item?.ctx?.min_length ?? 8 });
  if (type === "missing") return i18n.t("auth.requiredField", { field });
  const msg = typeof item?.msg === "string" ? item.msg : "";
  // Generic technical messages (patterns, type mismatches) are hidden.
  if (!msg || /pattern|regex|valid dictionary|input_type/i.test(msg)) return i18n.t("auth.invalidInput");
  return msg;
}
